/* Radiator Village — theme.js
   Vanilla, dependency-free, deferred. Everything degrades to a working page. */
(function () {
  'use strict';

  var CFG = window.RV || {};
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };
  var money = function (cents) {
    try {
      return new Intl.NumberFormat(CFG.locale || 'en-GB', {
        style: 'currency', currency: CFG.currency || 'GBP'
      }).format(cents / 100);
    } catch (e) { return (cents / 100).toFixed(2); }
  };

  /* ------------------------------------------------------------ reveals -- */
  function initReveal(root) {
    if (!CFG.anim || reduced || !('IntersectionObserver' in window)) {
      $$('[data-reveal]', root).forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var group = el.closest('[data-reveal-group]');
        var delay = 0;
        if (group) {
          var sibs = $$('[data-reveal]', group);
          delay = Math.min(sibs.indexOf(el), 8) * (CFG.stagger || 0);
        }
        el.style.transitionDelay = delay + 'ms';
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    $$('[data-reveal]', root).forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------- header -- */
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;
    var last = window.scrollY, ticking = false;
    function onScroll() {
      var y = window.scrollY;
      header.classList.toggle('is-stuck', y > 4);
      if (CFG.headerHide && !document.body.classList.contains('is-locked')) {
        if (y > last && y > 220) header.classList.add('is-hidden');
        else header.classList.remove('is-hidden');
      }
      last = y; ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
    }, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------ drawers -- */
  var openDrawer = null;
  function drawerOpen(id) {
    var d = document.getElementById(id);
    if (!d) return;
    if (openDrawer && openDrawer !== d) drawerClose();
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    openDrawer = d;
    var focusable = d.querySelector('[data-drawer-close], a, button, input');
    if (focusable) setTimeout(function () { focusable.focus(); }, 60);
  }
  function drawerClose() {
    if (!openDrawer) return;
    openDrawer.classList.remove('is-open');
    openDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    openDrawer = null;
  }
  function initDrawers() {
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-drawer-open]');
      if (opener) { e.preventDefault(); drawerOpen(opener.getAttribute('data-drawer-open')); return; }
      if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('drawer__scrim')) {
        e.preventDefault(); drawerClose();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') drawerClose();
    });
    // Collapsible sub-menus in mobile nav
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-submenu-toggle]');
      if (!t) return;
      e.preventDefault();
      var panel = t.parentElement.querySelector('[data-submenu]');
      if (!panel) return;
      var open = panel.hasAttribute('hidden');
      if (open) panel.removeAttribute('hidden'); else panel.setAttribute('hidden', '');
      t.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ----------------------------------------------------------- carousel -- */
  function initRails(root) {
    $$('[data-rail]', root).forEach(function (rail) {
      if (rail.dataset.railReady) return;
      rail.dataset.railReady = '1';
      var track = $('[data-rail-track]', rail);
      if (!track) return;
      var prev = $('[data-rail-prev]', rail);
      var next = $('[data-rail-next]', rail);
      var bar = $('[data-rail-bar]', rail);

      function step() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0;
        return first.getBoundingClientRect().width + gap;
      }
      function update() {
        var max = track.scrollWidth - track.clientWidth;
        var scrollable = max > 2;
        if (prev) { prev.disabled = track.scrollLeft <= 2; }
        if (next) { next.disabled = track.scrollLeft >= max - 2; }
        if (bar) {
          var visible = track.clientWidth / track.scrollWidth;
          var pos = max > 0 ? track.scrollLeft / max : 0;
          bar.style.transform = 'scaleX(' + Math.min(visible, 1) + ')';
          bar.style.marginLeft = (pos * (1 - Math.min(visible, 1)) * 100) + '%';
        }
        rail.classList.toggle('is-static', !scrollable);
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: reduced ? 'auto' : 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: reduced ? 'auto' : 'smooth' }); });
      track.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update);
      update();

      // drag to scroll (pointer devices only)
      if (CFG.railDrag && window.matchMedia('(pointer:fine)').matches) {
        var down = false, startX = 0, startScroll = 0, moved = 0;
        track.addEventListener('pointerdown', function (e) {
          if (e.button !== 0) return;
          down = true; moved = 0; startX = e.clientX; startScroll = track.scrollLeft;
          rail.classList.add('is-dragging');
        });
        window.addEventListener('pointermove', function (e) {
          if (!down) return;
          var dx = e.clientX - startX;
          if (Math.abs(dx) > 3) moved = Math.abs(dx);
          track.scrollLeft = startScroll - dx;
        });
        window.addEventListener('pointerup', function () {
          if (!down) return;
          down = false;
          rail.classList.remove('is-dragging');
          if (moved > 6) {
            track.addEventListener('click', function block(ev) {
              ev.preventDefault(); ev.stopPropagation();
              track.removeEventListener('click', block, true);
            }, true);
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------ gallery -- */
  function initGallery(root) {
    $$('[data-gallery]', root).forEach(function (g) {
      var slides = $('[data-gallery-slides]', g);
      var thumbs = $$('[data-gallery-thumb]', g);
      if (!slides) return;
      function setCurrent(i) {
        thumbs.forEach(function (t, n) { t.setAttribute('aria-current', n === i ? 'true' : 'false'); });
      }
      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () {
          var slide = slides.children[i];
          if (slide) slides.scrollTo({ left: slide.offsetLeft, behavior: reduced ? 'auto' : 'smooth' });
          setCurrent(i);
        });
      });
      slides.addEventListener('scroll', function () {
        var i = Math.round(slides.scrollLeft / slides.clientWidth);
        setCurrent(i);
      }, { passive: true });
      setCurrent(0);
    });
  }

  /* --------------------------------------------------------------- tabs -- */
  function initTabs(root) {
    $$('[data-tabs]', root).forEach(function (wrap) {
      var tabs = $$('[data-tab]', wrap);
      var panels = $$('[data-tabpanel]', wrap);
      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          tabs.forEach(function (t, n) { t.setAttribute('aria-selected', n === i ? 'true' : 'false'); });
          panels.forEach(function (p, n) { if (n === i) p.removeAttribute('hidden'); else p.setAttribute('hidden', ''); });
        });
        tab.addEventListener('keydown', function (e) {
          var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var nextTab = tabs[(i + dir + tabs.length) % tabs.length];
          nextTab.focus(); nextTab.click();
        });
      });
    });
  }

  /* ---------------------------------------------------------- quantity --- */
  function initQty(root) {
  $$('[data-qty]', root).forEach(function (box) {

    /* Prevent duplicate initialisation */
    if (box.dataset.rvQtyReady === '1') return;
    box.dataset.rvQtyReady = '1';

    var input = $('input', box);
    if (!input) return;

    $$('[data-qty-step]', box).forEach(function (btn) {

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var delta = parseInt(
          btn.getAttribute('data-qty-step'),
          10
        ) || 0;

        var min = parseInt(
          input.min || '1',
          10
        );

        var current = parseInt(
          input.value,
          10
        );

        if (isNaN(current) || current < min) {
          current = min;
        }

        input.value = Math.max(
          min,
          current + delta
        );

        input.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      });

    });
  });
}

  /* ---------------------------------------------------------------- cart -- */
  function toast(msg) {
    var el = $('[data-toast]');
    if (!el) return;
    el.querySelector('span').textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.classList.remove('is-visible'); }, 3200);
  }

  function refreshCart() {
  return fetch(CFG.routes.cart + '.js?_rv=' + Date.now(), {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json'
    },
    cache: 'no-store'
  })
    .then(function (r) {
      if (!r.ok) throw new Error('Could not refresh basket.');
      return r.json();
    })
    .then(function (cart) {

      /* Update the actual custom cart drawer */
      if (typeof window.__renderShopifyCart === 'function') {
        window.__renderShopifyCart(cart);
      }

      /* Update header cart count */
      $$('#cart-count, .cart-count, [data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      return cart;
    })
    .catch(function (error) {
      console.error('Cart refresh error:', error);
      return null;
    });
}

  function initCart() {
    // add to cart
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-product-form]');
      if (!form || !CFG.cartAjax) return;
      e.preventDefault();
      var btn = form.querySelector('[type=submit]');
      if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
      fetch(CFG.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
          if (!res.ok) { toast(res.body.description || res.body.message || 'Could not add that item'); return; }
          return refreshCart().then(function () {
            if (CFG.cartType === 'drawer') drawerOpen('cart-drawer');
            else toast('Added to your basket');
          });
        })
        .catch(function () {
          if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
          toast('Something went wrong. Please try again.');
        });
    });

    // change quantity / remove inside cart
    document.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-cart-remove]');
      if (!rm) return;
      e.preventDefault();
      updateLine(rm.getAttribute('data-cart-remove'), 0);
    });
    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      updateLine(input.getAttribute('data-cart-qty'), parseInt(input.value, 10) || 0);
    });
    function updateLine(key, qty) {
      var host = $('[data-cart-contents]');
      if (host) host.style.opacity = '.5';
      fetch(CFG.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function () { return refreshCart(); })
        .then(function () {
          var h = $('[data-cart-contents]');
          if (h) h.style.opacity = '';
          if (document.body.classList.contains('template-cart')) window.location.reload();
        });
    }
  }
/* ==========================================================================
   Radiator Village — cart refresh fix  (v2)
   Replaces refreshCart() and initCart() in theme.js

   PROBLEM
   refreshCart() fetched /cart.js and handed the JSON to
   window.__renderShopifyCart(cart) — a function that does not exist in
   theme.js. The `if` is always false, the JSON is discarded, and the drawer
   keeps the Liquid markup it was rendered with at page load
   ("Your basket is empty"). Only the count badge updated. The
   window.location.reload() on template-cart was a band-aid for this.

   FIX
   Shopify's Section Rendering API returns freshly rendered section HTML in
   the same request as the add/update. We swap that HTML into the page.

   REQUIRES two Liquid edits — add data-cart-section to the outer div of
   each cart section:

     sections/<cart drawer>.liquid
       <div class="drawer drawer--right" id="cart-drawer"
            data-cart-section="{{ section.id }}" ...>

     sections/<cart page>.liquid
       <div class="wrap section" data-cart-section="{{ section.id }}" ...>

   Section IDs are read from the DOM at runtime, so nothing is hardcoded and
   only sections actually present on the current page are requested.
   ========================================================================== */

/* Which cart sections exist on this page right now? ---------------------- */
function cartSectionIds() {
  return $$('[data-cart-section]')
    .map(function (el) { return el.getAttribute('data-cart-section'); })
    .filter(Boolean);
}

function sectionsParam() {
  return cartSectionIds().join(',');
}

function sectionsUrl() {
  return window.location.pathname + window.location.search;
}

/* Swap freshly rendered HTML into the live page -------------------------- */
function applyCartSections(sections) {
  if (!sections) return false;
  var applied = false;

  Object.keys(sections).forEach(function (id) {
    var html = sections[id];
    if (!html) return;

    var live = document.querySelector('[data-cart-section="' + id + '"]');
    if (!live) return;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-cart-section="' + id + '"]');
    if (!fresh) return;

    /* Prefer swapping just [data-cart-contents]. For the drawer this keeps
       the #cart-drawer element, its is-open class, the scrim and the close
       button intact — otherwise changing quantity inside an open drawer
       would rebuild the drawer and slam it shut on the customer. */
    var liveInner = live.querySelector('[data-cart-contents]');
    var freshInner = fresh.querySelector('[data-cart-contents]');

    if (liveInner && freshInner) {
      liveInner.innerHTML = freshInner.innerHTML;
      initAll(liveInner);
    } else {
      live.innerHTML = fresh.innerHTML;
      initAll(live);
    }

    applied = true;
  });

  return applied;
}

/* Count badges ------------------------------------------------------------ */
function paintCartCount(cart) {
  $$('#cart-count, .cart-count, [data-cart-count]').forEach(function (el) {
    el.textContent = cart.item_count;
    // Don't hard-code display:flex — let the stylesheet decide.
    el.hidden = cart.item_count === 0;
    el.classList.toggle('is-empty', cart.item_count === 0);
  });
}

/* Main refresh ------------------------------------------------------------ */
function refreshCart(sections) {
  var rendered = applyCartSections(sections);

  return fetch(CFG.routes.cart + '.js?_rv=' + Date.now(), {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    cache: 'no-store'
  })
    .then(function (r) {
      if (!r.ok) throw new Error('Could not refresh basket.');
      return r.json();
    })
    .then(function (cart) {
      paintCartCount(cart);

      // Still support an external renderer if one is ever loaded.
      if (typeof window.__renderShopifyCart === 'function') {
        window.__renderShopifyCart(cart);
        rendered = true;
      }

      // Nothing rendered the markup — fetch the sections separately.
      var param = sectionsParam();
      if (!rendered && param) {
        return fetch(CFG.routes.cart + '?sections=' + encodeURIComponent(param), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        })
          .then(function (r) { return r.json(); })
          .then(function (s) { applyCartSections(s); return cart; })
          .catch(function () { return cart; });
      }

      return cart;
    })
    .catch(function (error) {
      console.error('Cart refresh error:', error);
      return null;
    });
}

/* Cart interactions ------------------------------------------------------- */
function initCart() {

  /* ---- add to basket ---- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-product-form]');
    if (!form || !CFG.cartAjax) return;
    e.preventDefault();

    var btn = form.querySelector('[type=submit]');
    if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }

    var body = new FormData(form);
    var param = sectionsParam();
    if (param) {
      body.append('sections', param);
      body.append('sections_url', sectionsUrl());
    }

    fetch(CFG.routes.cartAdd, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (r) {
        return r.json().then(function (j) { return { ok: r.ok, body: j }; });
      })
      .then(function (res) {
        if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }

        if (!res.ok) {
          toast(res.body.description || res.body.message || 'Could not add that item');
          return;
        }

        return refreshCart(res.body.sections).then(function () {
          if (CFG.cartType === 'drawer') drawerOpen('cart-drawer');
          else toast('Added to your basket');
        });
      })
      .catch(function () {
        if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
        toast('Something went wrong. Please try again.');
      });
  });

  /* ---- remove a line ---- */
  document.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-cart-remove]');
    if (!rm) return;
    e.preventDefault();
    updateLine(rm.getAttribute('data-cart-remove'), 0);
  });

  /* ---- change quantity ---- */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    updateLine(input.getAttribute('data-cart-qty'), parseInt(input.value, 10) || 0);
  });

  function updateLine(key, qty) {
    var host = $('[data-cart-contents]');
    if (host) host.style.opacity = '.5';

    var payload = { id: key, quantity: qty };
    var param = sectionsParam();
    if (param) {
      payload.sections = param;
      payload.sections_url = sectionsUrl();
    }

    fetch(CFG.routes.cartChange, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Could not update basket.');
        return r.json();
      })
      .then(function (json) {
        /* Render from the response so stock caps are honoured: ask for 10
           when 3 are available and Shopify returns 3, so the input snaps to
           the real number instead of showing one that isn't. */
        return refreshCart(json.sections);
      })
      .then(function () {
        var h = $('[data-cart-contents]');
        if (h) h.style.opacity = '';
        // NOTE: the old window.location.reload() on template-cart is gone.
      })
      .catch(function (err) {
        console.error(err);
        var h = $('[data-cart-contents]');
        if (h) h.style.opacity = '';
        toast('Could not update your basket. Please try again.');
      });
  }
}

  /* -------------------------------------------------- predictive search -- */
  function initSearch() {
    $$('[data-search-form]').forEach(function (form) {
      var input = $('input[type=search], input[name=q]', form);
      var panel = $('[data-search-results]', form.parentElement) || $('[data-search-results]', form);
      if (!input || !panel || !CFG.predictive) return;
      var timer, controller;
      input.addEventListener('input', function () {
        clearTimeout(timer);
        var q = input.value.trim();
        if (q.length < 2) { panel.setAttribute('hidden', ''); return; }
        timer = setTimeout(function () {
          if (controller) controller.abort();
          controller = new AbortController();
          var url = CFG.routes.predictive + '?q=' + encodeURIComponent(q) +
            '&resources[type]=' + (CFG.predictiveTypes || 'product') +
            '&resources[limit]=6&section_id=predictive-search';
          fetch(url, { signal: controller.signal })
            .then(function (r) { return r.text(); })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var results = doc.querySelector('[data-search-results]');
              panel.innerHTML = results ? results.innerHTML : '';
              panel.removeAttribute('hidden');
            })
            .catch(function () { });
        }, 220);
      });
      document.addEventListener('click', function (e) {
        if (!form.contains(e.target) && !panel.contains(e.target)) panel.setAttribute('hidden', '');
      });
      input.addEventListener('keydown', function (e) { if (e.key === 'Escape') panel.setAttribute('hidden', ''); });
    });
  }

  /* ------------------------------------------------------------ variants -- */
  function initVariants(root) {
    $$('[data-variant-picker]', root).forEach(function (picker) {
      var form = picker.closest('form[data-product-form]') || $('form[data-product-form]');
      var data = JSON.parse($('[data-variant-json]', picker).textContent || '[]');
      var idField = form ? form.querySelector('[name=id]') : null;
      var optionCount = parseInt(picker.getAttribute('data-option-count'), 10) || 1;

      function selected() {
        var out = [];
        for (var i = 1; i <= optionCount; i++) {
          var chosen = picker.querySelector('[data-option-index="' + i + '"][aria-checked=true]');
          out.push(chosen ? chosen.getAttribute('data-value') : null);
        }
        return out;
      }
      function match(values) {
        return data.find(function (v) {
          return values.every(function (val, i) { return v.options[i] === val; });
        });
      }
      function paint() {
        var values = selected();
        var variant = match(values);

        // mark unavailable combinations
        $$('[data-option-index]', picker).forEach(function (btn) {
          var idx = parseInt(btn.getAttribute('data-option-index'), 10) - 1;
          var test = values.slice();
          test[idx] = btn.getAttribute('data-value');
          var found = data.find(function (v) {
            return test.every(function (val, i) { return val === null || v.options[i] === val; });
          });
          btn.classList.toggle('is-unavailable', !found || !found.available);
        });

        $$('[data-option-index]', picker).forEach(function (btn) {
          var idx = btn.getAttribute('data-option-index');
          var label = picker.querySelector('[data-option-label="' + idx + '"]');
          if (label && btn.getAttribute('aria-checked') === 'true') label.textContent = btn.getAttribute('data-value');
        });

        if (!variant) return;
        if (idField) idField.value = variant.id;

        var price = $('[data-price]');
        if (price) price.innerHTML = money(variant.price);
        var was = $('[data-compare]');
        if (was) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            was.innerHTML = money(variant.compare_at_price); was.hidden = false;
          } else { was.hidden = true; }
        }
        var exvat = $('[data-exvat]');
        if (exvat) exvat.innerHTML = money(Math.round(variant.price / 1.2)) + ' (ex. VAT)';
        var fin = $('[data-finance]');
        if (fin && CFG.financeMonths) {
          fin.textContent = money(Math.round(variant.price / CFG.financeMonths));
        }
        var stock = $('[data-stock]');
        if (stock) {
          stock.textContent = variant.available ? (stock.getAttribute('data-in') || 'In stock') : (stock.getAttribute('data-out') || 'Sold out');
          stock.classList.toggle('pdp__stock--out', !variant.available);
        }
        var submit = form ? form.querySelector('[type=submit]') : null;
        if (submit) {
          submit.disabled = !variant.available;
          var t = submit.querySelector('[data-submit-text]');
          if (t) t.textContent = variant.available ? (submit.getAttribute('data-add') || 'Add to basket') : 'Sold out';
        }
        // swap gallery to the variant image
        if (variant.featured_media_index != null) {
          var thumb = picker.ownerDocument.querySelector('[data-gallery-thumb="' + variant.featured_media_index + '"]');
          if (thumb) thumb.click();
        }
        // reflect in URL without a reload
        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
      }

      picker.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-option-index]');
        if (!btn) return;
        var idx = btn.getAttribute('data-option-index');
        $$('[data-option-index="' + idx + '"]', picker).forEach(function (b) { b.setAttribute('aria-checked', 'false'); });
        btn.setAttribute('aria-checked', 'true');
        paint();
      });
      picker.addEventListener('change', function (e) {
        if (e.target.matches('select[data-option-select]')) {
          var idx = e.target.getAttribute('data-option-select');
          $$('[data-option-index="' + idx + '"]', picker).forEach(function (b) {
            b.setAttribute('aria-checked', b.getAttribute('data-value') === e.target.value ? 'true' : 'false');
          });
          paint();
        }
      });
      paint();
    });
  }

  /* ------------------------------------------------------ sticky buy bar -- */
  function initStickyBuy() {
    var bar = $('[data-sticky-buy]');
    var anchor = $('[data-buy-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ----------------------------------------------------- BTU calculator -- */
  function initCalc(root) {
    $$('[data-calc]', root).forEach(function (calc) {
      var factors = JSON.parse(calc.getAttribute('data-factors') || '{}');
      var out = $('[data-calc-value]', calc);
      var unitEl = $('[data-calc-unit]', calc);
      var alt = $('[data-calc-alt]', calc);
      var roomOut = $('[data-calc-room]', calc);
      if (!out) return;
      function num(el) { var n = parseFloat(el && el.value); return isNaN(n) || n < 0 ? 0 : n; }
      function run() {
        var w = num($('[data-calc-w]', calc));
        var l = num($('[data-calc-l]', calc));
        var h = num($('[data-calc-h]', calc));
        var roomSel = $('[data-calc-room-select]', calc);
        var keys = Object.keys(factors);
        if (!keys.length) return;
        var room = roomSel && roomSel.value ? roomSel.value : keys[0];
        var vol = w * l * h;
        var btu = Math.round((vol * (factors[room] || 150)) / 50) * 50;
        var watts = Math.round((btu / 3.412) / 10) * 10;
        var showWatts = calc.getAttribute('data-unit') === 'Watts';
        var fmt = function (n) { return n.toLocaleString(CFG.locale || 'en-GB'); };
        out.textContent = fmt(showWatts ? watts : btu);
        if (unitEl) unitEl.textContent = showWatts ? 'Watts' : 'BTU';
        if (alt) alt.textContent = showWatts ? fmt(btu) + ' BTU' : fmt(watts) + ' Watts';
        if (roomOut) roomOut.textContent = room.toLowerCase();
        var link = $('[data-calc-link]', calc);
        if (link) {
          var base = link.getAttribute('data-base') || link.getAttribute('href');
          link.setAttribute('href', base + (base.indexOf('?') > -1 ? '&' : '?') + 'btu=' + btu);
        }
      }
      calc.addEventListener('input', run);
      calc.addEventListener('change', run);
      run();
    });
  }

  /* ----------------------------------------------------------- parallax -- */
  function initParallax() {
    if (!CFG.parallax || reduced) return;
    var targets = $$('[data-parallax]');
    if (!targets.length) return;
    var ticking = false;
    function frame() {
      targets.forEach(function (el) {
        var rect = el.parentElement.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
        var progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        var amount = parseFloat(el.getAttribute('data-parallax')) || 12;
        el.style.transform = 'translate3d(0,' + (progress * amount * -1).toFixed(2) + '%,0)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(frame); }
    }, { passive: true });
    frame();
  }

  /* ------------------------------------------------------------- images -- */
  function initImages(root) {
    $$('.blurup', root).forEach(function (wrap) {
      var img = wrap.querySelector('img');
      if (!img) return;
      if (img.complete && img.naturalWidth) wrap.classList.add('is-loaded');
      else img.addEventListener('load', function () { wrap.classList.add('is-loaded'); }, { once: true });
    });
  }

  /* -------------------------------------------------------------- misc --- */
  function initMisc() {
    // back to top + progress
    var top = $('[data-to-top]');
    var prog = $('[data-progress]');
    if (top || prog) {
      var t = false;
      window.addEventListener('scroll', function () {
        if (t) return; t = true;
        requestAnimationFrame(function () {
          var y = window.scrollY;
          if (top) top.classList.toggle('is-visible', y > 700);
          if (prog) {
            var max = document.documentElement.scrollHeight - window.innerHeight;
            prog.style.width = (max > 0 ? (y / max) * 100 : 0) + '%';
          }
          t = false;
        });
      }, { passive: true });
    }
    if (top) top.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    });

    // prefetch on hover
    if (CFG.prefetch && 'connection' in navigator ? !navigator.connection.saveData : CFG.prefetch) {
      var seen = {};
      document.addEventListener('mouseover', function (e) {
        var a = e.target.closest('a[href^="/"]');
        if (!a || seen[a.href] || a.hasAttribute('data-no-prefetch')) return;
        seen[a.href] = 1;
        var link = document.createElement('link');
        link.rel = 'prefetch'; link.href = a.href;
        document.head.appendChild(link);
      }, { passive: true });
    }

    // page fade
    if (CFG.pageFade) requestAnimationFrame(function () { document.body.classList.add('is-ready'); });
  }

  /* -------------------------------------------------- recommendations --- */
  function initRecommendations() {
    var host = $('[data-related-url]');
    if (!host || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      fetch(host.getAttribute('data-related-url'))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('.wrap');
          if (fresh && fresh.querySelector('[data-rail]')) {
            host.outerHTML = fresh.outerHTML;
            initAll(document);
          }
        })
        .catch(function () { });
    }, { rootMargin: '400px' });
    io.observe(host);
  }

  /* ---------------------------------------------------------- bootstrap -- */
  function initAll(root) {
    root = root || document;
    initReveal(root);
    initRails(root);
    initGallery(root);
    initTabs(root);
    initQty(root);
    initVariants(root);
    initCalc(root);
    initImages(root);
  }

  function boot() {
    initAll(document);
    initHeader();
    initDrawers();
    initCart();
    initSearch();
    initStickyBuy();
    initRecommendations();
    initParallax();
    initMisc();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-init when a section is re-rendered
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    initHeader(); initParallax(); initStickyBuy();
  });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var rail = e.target.closest('[data-rail-track]');
    if (rail) e.target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  });

  window.RVTheme = { refreshCart: refreshCart, toast: toast, drawerOpen: drawerOpen, drawerClose: drawerClose };
})();
